import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowIcon } from '../../assets/icons/down-arrow.svg';
import { ReactComponent as Search } from '../../assets/icons/search.svg';

type Option = {
  value: string | number;
  label: string;
};

interface SelectFieldProperties {
  inputLabel?: string;
  options: Option[]; // Array of objects with value and label
  placeholder?: string;
  selectedValue: string | number;
  name: string;
  enableSearch?: boolean;
  onChangeValue: (name: string, value: any) => void;
  valueRequired?: boolean;
  error?: boolean;
  errorMessage?: string;
  svgIcon?: JSX.Element;
  fontSize?: 'small' | 'medium' | 'large';
  allowCustomInput?: boolean;
  handleCustomizedInput?: (value: string | number) => void;
  loading?: boolean;
  disabled?: boolean;
  disabledAutoSorting?: boolean; // Optional prop to disable auto-sorting of options
}

/**
 * Select Box filed function component
 */
function SelectField({
  inputLabel,
  options,
  name,
  placeholder,
  selectedValue,
  onChangeValue,
  error,
  errorMessage,
  enableSearch,
  valueRequired,
  svgIcon,
  fontSize = 'large',
  allowCustomInput = false,
  handleCustomizedInput,
  loading = false,
  disabled = false,
  disabledAutoSorting = false, // Default to false
}: SelectFieldProperties) {
  const reference = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { i18n } = useTranslation('sharedComponent');
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';

  const [open, setOpen] = useState<string>('close');
  const [style, setStyle] = useState<any>({});
  const [valueToShow, setValueToShow] = useState<string | number>(selectedValue);
  // 🔥 Added for Search
  const [searchText, setSearchText] = useState('');
  /**
   * Handle the value change function
   */
  // const handleValueChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
  const handleValueChange = (changeValue: any) => {
    onChangeValue(name, changeValue);
    setOpen('close');
  };

  /**
   * Handle the close th drop down function
   * @param event - MouseEvent for outside click
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setOpen('close');
    }
  };

  /**
   * Get the dropdown position
   */
  const openDropDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    const { offsetHeight, offsetWidth } = event.currentTarget;
    let paddingValue: string | undefined;
    if (enableSearch) {
      paddingValue = direction === 'rtl' ? '14px 8px 15px 6px' : '14px 6px 15px 8px';
    }
    if (window.innerHeight > event.clientY + 300) {
      setStyle({
        top: `${inputLabel ? offsetHeight + 30 : offsetHeight}px`,
        width: `${offsetWidth}px`,
        padding: paddingValue,
      });
    } else {
      setStyle({
        bottom: `${offsetHeight}px`,
        width: `${offsetWidth}px`,
        borderBottomColor: '#6c757d',
        borderTopColor: '#e3e8f1',
        borderRadius: '8px 8px 0 0',
        padding: paddingValue,
      });
    }
    setOpen('open');
  };

  // ✅ Apply sorting only if auto-sorting is NOT disabled
  const sortedOptions = React.useMemo(() => {
    if (disabledAutoSorting) return options; // Sorting disabled → return as-is
    if (!options || options.length === 0) return [];

    // 1️⃣ Find optional “All Station” entry (by value or label)
    const allStationIndex = options.findIndex(
      (opt) => opt.value === 'all' || opt.label.toLowerCase() === 'all station',
    );

    let allStationOption: Option | null = null;
    const restOptions = [...options];

    // 2️⃣ Remove it from the list temporarily
    if (allStationIndex !== -1) {
      allStationOption = restOptions[allStationIndex];
      restOptions.splice(allStationIndex, 1);
    }

    // 3️⃣ Sort others alphabetically
    restOptions.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

    // 4️⃣ Put “All Station” on top only when it exists
    return allStationOption ? [allStationOption, ...restOptions] : restOptions;
  }, [options, disabledAutoSorting]);

  useEffect(() => {
    setValueToShow(selectedValue);
  }, [selectedValue]);

  useEffect((): void => {
    document.addEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce functionality with 1s delay
  useEffect(() => {
    // Clear existing debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set a new debounce timer
    debounceTimer.current = setTimeout(() => {
      // pass the latest value
      if (handleCustomizedInput && selectedValue !== valueToShow && valueToShow) {
        handleCustomizedInput(valueToShow);
      }
    }, 1000); // 01 second | 1000 millisecond

    // Cleanup on unmount or valueToShow change
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [valueToShow]);

  return (
    <div className={`custom-select-wrap ${fontSize}`} dir={direction}>
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div
        className={`custom-select-container ${error && 'error'} ${open}`}
        onClick={openDropDown}
        role="presentation"
        style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
        {svgIcon && <div className="frond-icon">{svgIcon}</div>}
        <div className="view-value">
          {allowCustomInput ? (
            <input
              value={valueToShow}
              onChange={(event) => !loading && setValueToShow(event.target.value)}
              className="custom-value-wrap"
              disabled={disabled}
            />
          ) : (
            <p
              className={
                selectedValue
                  ? 'select-label' // when label is shown → red
                  : '' // when placeholder is shown → black
              }>
              {options.find((option) => option.value === selectedValue)?.label || placeholder}
            </p>
          )}
        </div>
        <span className="custom-icon">
          <ArrowIcon />
        </span>
      </div>
      {error && <p className="text-danger">{errorMessage}</p>}
      <div className={`dropdown-items-list ${open}`} ref={reference} style={style}>
        {/* 🔍 Search only if enabled */}
        {enableSearch && !disabled && (
          <div className="select-box-search">
            <input
              type="text"
              className="search-box"
              placeholder="Search..."
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onClick={(event) => event.stopPropagation()}
            />
            <span>
              <Search />
            </span>
          </div>
        )}

        {!disabled &&
          sortedOptions
            .filter(
              (option) =>
                !enableSearch || option.label.toLowerCase().includes(searchText.toLowerCase()),
            )
            .map((option) => (
              <div
                className="items"
                key={option.value}
                onClick={() => handleValueChange(option.value)}
                role="presentation">
                {option.label}
              </div>
            ))}
        {enableSearch &&
          sortedOptions.filter((opt) => opt.label.toLowerCase().includes(searchText.toLowerCase()))
            .length === 0 && <div className="items no-data">No results found</div>}
      </div>
    </div>
  );
}

SelectField.defaultProps = {
  inputLabel: '',
  placeholder: '',
  valueRequired: false,
  error: false,
  errorMessage: '',
  svgIcon: null,
  fontSize: 'large',
  allowCustomInput: false,
  handleCustomizedInput: null,
  loading: false,
  disabled: false,
  enableSearch: false,
  disabledAutoSorting: false, // Default to false
};

export default SelectField;
